import { AnimatePresence, motion } from "motion/react";
import React from "react";
import { UI_MOTION } from "../../../config/uiMotion.configuration";
import { useReducedMotion } from "../../../hooks/useReducedMotion.hook";
import stopParentIcon from "../../../image/icons/map/stopPlatform/stopParentIcon.svg";
import {
    isUiMotionEnabled,
    resolveUiEaseOut,
    resolveUiSpring,
    UI_MOTION_INSTANT_TRANSITION,
} from "../../shared/motion/uiMotion.tools";
import {
    STOP_POPUP_ARRIVAL_TIME_ROW_CLASS_NAME,
    STOP_POPUP_COUNTDOWN_PILL_VALUE_CLASS_NAME,
} from "./stopPopup.layout.constants";

/**
 * Clipped viewport for countdown ticks — fixed size keeps the pill stable while
 * values slide vertically (odometer-style).
 */
const STOP_POPUP_COUNTDOWN_PILL_VALUE_TICK_VIEWPORT_CLASS_NAME =
    `${ STOP_POPUP_COUNTDOWN_PILL_VALUE_CLASS_NAME } relative overflow-hidden`;

/** Absolutely stacked layer, centered in the tick viewport. */
const STOP_POPUP_COUNTDOWN_TICK_LAYER_CLASS_NAME =
    "absolute inset-0 flex items-center justify-center";

/** Old value exits upward; incoming value rises from below. */
const STOP_POPUP_COUNTDOWN_TICK_MOTION = {
    "animate": {
        "opacity": 1,
        "y":       "0%",
    },
    "enter": {
        "opacity": 1,
        "y":       "100%",
    },
    "exit": {
        "opacity": 1,
        "y":       "-100%",
    },
} as const;

/**
 * Builds the transition for countdown minute ticks (paired enter + exit).
 *
 * @param reducedMotion Whether the user prefers reduced motion.
 * @returns Motion transition for pill value ticks.
 */
const buildStopPopupCountdownTickTransition = ( reducedMotion: boolean ) => {
    return resolveUiEaseOut( reducedMotion, UI_MOTION.duration.fastMs );
};

interface ArrivalCountdownAbsent { "kind": "absent" }

interface ArrivalCountdownImminent { "kind": "imminent" }

interface ArrivalCountdownMinutes {
    "kind":    "minutes";
    "minutes": number;
}

export type ArrivalCountdownDisplay = ArrivalCountdownAbsent |
  ArrivalCountdownImminent |
  ArrivalCountdownMinutes;

/**
 * Derives how to show minutes-until-arrival from realtime data.
 *
 * @param realtimeValue ISO arrival timestamp or null.
 * @param nowMs Current clock in milliseconds.
 * @returns Countdown display variant for the bus/tram branch.
 * Zero or negative offset maps to {@link ArrivalCountdownImminent} so the UI avoids showing `0'`.
 */
export const getArrivalCountdownDisplay = (
    realtimeValue: string | null,
    nowMs: number,
): ArrivalCountdownDisplay => {
    if ( realtimeValue === null ) {
        return { "kind": "absent" };
    }

    const realtimeDate = new Date( realtimeValue );

    if ( Number.isNaN( realtimeDate.getTime() ) ) {
        return { "kind": "absent" };
    }

    const diffMinutes = Math.round( ( realtimeDate.getTime() - nowMs ) / 60000 );

    if ( diffMinutes <= 0 ) {
        return { "kind": "imminent" };
    }

    return {
        "kind":    "minutes",
        "minutes": diffMinutes,
    };
};

interface StopArrivalClockSvgProps { "color": string }

const StopArrivalClockSvg: React.FC<StopArrivalClockSvgProps> = ( { color } ) => (
    <svg
        aria-hidden
        className="w-4 h-4 flex-shrink-0"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        style={{ "color": color }}
        viewBox="0 0 24 24"
    >
        <path
            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

interface StopArrivalImminentStopPoleIconProps { "reducedMotion": boolean }

/**
 * Due-now indicator inside the countdown pill, replacing the minutes label.
 * Uses the same stop pole asset as the map, sized like {@link StopArrivalClockSvg}.
 */
const StopArrivalImminentStopPoleIcon: React.FC<StopArrivalImminentStopPoleIconProps> = ( { reducedMotion } ) => {
    const animationsEnabled = isUiMotionEnabled( reducedMotion );
    const pulseClassName = animationsEnabled ? " animate-pulse" : "";

    return (
        <span
            aria-label="Arrivée imminente"
            className={`inline-flex h-4 w-4 flex-shrink-0 items-center justify-center${ pulseClassName }`}
            role="status"
        >
            <img
                alt=""
                className="h-4 w-4 object-contain select-none pointer-events-none"
                decoding="async"
                draggable={false}
                src={stopParentIcon}
            />
        </span>
    );
};

interface StopArrivalTimeMotionProps {

    /** Countdown display derived from realtime arrival. */
    "arrivalCountdown": ArrivalCountdownDisplay;

    /** When true, skip countdown tick micro-animations (e.g. during skeleton reveal). */
    "deferMicroMotion"?: boolean;

    /** Classes for the minutes badge shell. */
    "countdownBadgeClassName": string;

    /** Inline styles for the minutes badge shell. */
    "countdownBadgeStyle": React.CSSProperties;

    /** Clock icon color token. */
    "clockIconColor": string;

    /** Formatted HH:MM arrival time. */
    "formattedArrivalTime": string;

    /** Pill text color for the row. */
    "pillTextColor": string;
}

/**
 * Renders the minutes label or imminent icon inside the countdown pill value slot.
 *
 * @param props Pill value slot props.
 * @returns Minutes text or imminent stop pole icon.
 */
interface StopArrivalCountdownPillValueProps {
    "arrivalCountdown":   ArrivalCountdownDisplay;
    "microMotionEnabled": boolean;
    "reducedMotion":      boolean;
}

const StopArrivalCountdownPillValue: React.FC<StopArrivalCountdownPillValueProps> = ( {
    arrivalCountdown,
    microMotionEnabled,
    reducedMotion,
} ) => {
    const tickTransition = microMotionEnabled
        ? buildStopPopupCountdownTickTransition( reducedMotion )
        : UI_MOTION_INSTANT_TRANSITION;

    if ( arrivalCountdown.kind === "minutes" ) {
        const minutesLabel = `${ arrivalCountdown.minutes }'`;

        if ( !microMotionEnabled ) {
            return (
                <span className={STOP_POPUP_COUNTDOWN_PILL_VALUE_CLASS_NAME}>
                    {minutesLabel}
                </span>
            );
        }

        return (
            <span className={STOP_POPUP_COUNTDOWN_PILL_VALUE_TICK_VIEWPORT_CLASS_NAME}>
                <AnimatePresence
                    initial={false}
                    mode="sync"
                >
                    <motion.span
                        animate={STOP_POPUP_COUNTDOWN_TICK_MOTION.animate}
                        className={STOP_POPUP_COUNTDOWN_TICK_LAYER_CLASS_NAME}
                        exit={STOP_POPUP_COUNTDOWN_TICK_MOTION.exit}
                        initial={STOP_POPUP_COUNTDOWN_TICK_MOTION.enter}
                        key={arrivalCountdown.minutes}
                        transition={tickTransition}
                    >
                        {minutesLabel}
                    </motion.span>
                </AnimatePresence>
            </span>
        );
    }

    if ( !microMotionEnabled ) {
        return (
            <span className={STOP_POPUP_COUNTDOWN_PILL_VALUE_CLASS_NAME}>
                <StopArrivalImminentStopPoleIcon reducedMotion={reducedMotion} />
            </span>
        );
    }

    return (
        <span className={STOP_POPUP_COUNTDOWN_PILL_VALUE_TICK_VIEWPORT_CLASS_NAME}>
            <AnimatePresence
                initial={false}
                mode="sync"
            >
                <motion.span
                    animate={STOP_POPUP_COUNTDOWN_TICK_MOTION.animate}
                    className={STOP_POPUP_COUNTDOWN_TICK_LAYER_CLASS_NAME}
                    exit={STOP_POPUP_COUNTDOWN_TICK_MOTION.exit}
                    initial={STOP_POPUP_COUNTDOWN_TICK_MOTION.enter}
                    key="imminent"
                    transition={tickTransition}
                >
                    <StopArrivalImminentStopPoleIcon reducedMotion={reducedMotion} />
                </motion.span>
            </AnimatePresence>
        </span>
    );
};

/**
 * Animated bus/tram arrival countdown and clock time for the stop popup.
 *
 * @param props Arrival time motion props.
 * @returns Animated countdown row fragment.
 */
const StopArrivalTimeMotion: React.FC<StopArrivalTimeMotionProps> = ( {
    arrivalCountdown,
    clockIconColor,
    countdownBadgeClassName,
    countdownBadgeStyle,
    deferMicroMotion = false,
    formattedArrivalTime,
    pillTextColor,
} ) => {
    const reducedMotion = useReducedMotion();
    const microMotionEnabled = isUiMotionEnabled( reducedMotion ) && !deferMicroMotion;

    const countdownIndicator = arrivalCountdown.kind === "absent"
        ? <StopArrivalClockSvg color={clockIconColor} />
        : (
            <div
                className={countdownBadgeClassName}
                style={countdownBadgeStyle}
            >
                <StopArrivalClockSvg color={clockIconColor} />
                <StopArrivalCountdownPillValue
                    arrivalCountdown={arrivalCountdown}
                    microMotionEnabled={microMotionEnabled}
                    reducedMotion={reducedMotion}
                />
            </div>
        );

    const timeNode = (
        <span className="font-bold">
            {formattedArrivalTime}
        </span>
    );

    return (
        <div
            className={STOP_POPUP_ARRIVAL_TIME_ROW_CLASS_NAME}
            style={{ "color": pillTextColor }}
        >
            {countdownIndicator}
            {timeNode}
        </div>
    );
};

interface StopTrainDelayMotionProps {

    /** Delay minutes label, e.g. `(+3)`. */
    "delayLabel": string | null;

    /** Text color for the delay badge. */
    "delayColor": string;
}

/**
 * Collapse/expand animation for train delay minutes in the stop popup.
 *
 * @param props Delay motion props.
 * @returns Animated delay label or null.
 */
export const StopTrainDelayMotion: React.FC<StopTrainDelayMotionProps> = ( {
    delayColor,
    delayLabel,
} ) => {
    const reducedMotion = useReducedMotion();
    const animationsEnabled = isUiMotionEnabled( reducedMotion );

    if ( delayLabel === null ) {
        return null;
    }

    if ( !animationsEnabled ) {
        return (
            <span
                className="font-bold text-xs"
                style={{ "color": delayColor }}
            >
                {delayLabel}
            </span>
        );
    }

    return (
        <AnimatePresence initial={false}>
            <motion.span
                animate={{
                    "height":  "auto",
                    "opacity": 1,
                    "scale":   1,
                }}
                className="font-bold text-xs overflow-hidden"
                exit={{
                    "height":  0,
                    "opacity": 0,
                    "scale":   0.9,
                }}
                initial={false}
                key={delayLabel}
                style={{ "color": delayColor }}
                transition={resolveUiSpring( reducedMotion, "gentle" )}
            >
                {delayLabel}
            </motion.span>
        </AnimatePresence>
    );
};

export default StopArrivalTimeMotion;
